import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SubscriptionTierIndicator from '../../components/ui/SubscriptionTierIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CraneDetailsForm from './components/CraneDetailsForm';
import ValuationResults from './components/ValuationResults';
import DepreciationAnalysis from './components/DepreciationAnalysis';
import MarketInsights from './components/MarketInsights';
import ValuationHistory from './components/ValuationHistory';

const CustomCraneValuation = () => {
  const [formData, setFormData] = useState({});
  const [valuationResults, setValuationResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [savedConfigurations, setSavedConfigurations] = useState([]);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/crane-search-dashboard' },
    { label: 'Custom Valuation', path: null }
  ];

  const handleFormSubmit = async (craneData) => {
    setIsLoading(true);
    setFormData(craneData);

    // Simulate valuation calculation
    setTimeout(() => {
      const mockValuation = generateMockValuation(craneData);
      setValuationResults(mockValuation);
      setCurrentStep(2);
      setIsLoading(false);
    }, 2500);
  };

  const generateMockValuation = (data) => {
    // Base value calculation based on crane type and capacity
    const baseValues = {
      'Mobile Crane': 800000,
      'Rough Terrain': 650000,
      'All Terrain': 1200000,
      'Crawler Crane': 2500000,
      'Tower Crane': 1800000
    };

    const baseValue = baseValues?.[data?.craneType] || 800000;
    const capacityMultiplier = (data?.capacity || 100) / 100;
    const yearMultiplier = Math.max(0.3, 1 - ((2024 - (data?.year || 2020)) * 0.08));
    const conditionMultiplier = (data?.condition || 3) / 5;
    const hoursMultiplier = Math.max(0.4, 1 - ((data?.hours || 3000) / 10000) * 0.4);

    const fairMarketValue = baseValue * capacityMultiplier * yearMultiplier * conditionMultiplier * hoursMultiplier;
    const minValue = fairMarketValue * 0.85;
    const maxValue = fairMarketValue * 1.15;

    return {
      fairMarketValue: Math.round(fairMarketValue),
      valueRange: {
        min: Math.round(minValue),
        max: Math.round(maxValue)
      },
      confidenceScore: Math.min(95, Math.max(65, 85 + (data?.condition || 3) * 2 - Math.abs((data?.hours || 3000) - 3000) / 500)),
      depreciationRate: 8 + Math.random() * 4,
      regionalVariation: {
        current: 0,
        high: 15,
        low: -12
      },
      marketFactors: {
        demand: Math.random() > 0.5 ? 'High' : 'Moderate',
        supply: Math.random() > 0.5 ? 'Limited' : 'Adequate',
        trend: Math.random() > 0.5 ? 'Increasing' : 'Stable'
      },
      recommendations: [
        'Consider maintenance history when pricing',
        'Market conditions favor sellers in Q4',
        'Similar models selling 15% above average'
      ]
    };
  };

  const handleSaveConfiguration = () => {
    const newConfig = {
      id: Date.now(),
      name: `${formData?.make} ${formData?.model} - ${new Date()?.toLocaleDateString()}`,
      data: formData,
      valuation: valuationResults,
      createdAt: new Date()?.toISOString()
    };
    setSavedConfigurations(prev => [newConfig, ...prev]);
  };

  const handleReset = () => {
    setFormData({});
    setValuationResults(null);
    setCurrentStep(1);
  };

  const handleDownloadReport = () => {
    // Generate comprehensive PDF report
    console.log('Generating PDF report...', { formData, valuationResults });
  };

  const handleCompareModels = () => {
    window.location.href = `/comparison-tool?make=${formData?.make}&model=${formData?.model}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar />
      
      <main className="container mx-auto px-6 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="space-y-2">
            <Breadcrumbs items={breadcrumbItems} />
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Calculator" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Custom Crane Valuation</h1>
                <p className="text-text-secondary">Get personalized valuations with depreciation analysis and market insights</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <SubscriptionTierIndicator tier="pro" />
            {valuationResults && (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={handleDownloadReport}
                  iconName="Download"
                  iconPosition="left"
                  size="sm"
                >
                  Download Report
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCompareModels}
                  iconName="GitCompare"
                  iconPosition="left"
                  size="sm"
                >
                  Compare Models
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="bg-surface border border-border rounded-lg shadow-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-text-primary">Valuation Process</h3>
            {valuationResults && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Start New Valuation
              </Button>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-primary' : 'text-text-secondary'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= 1 ? 'bg-primary text-white' : 'bg-muted text-text-secondary'
              }`}>
                1
              </div>
              <span className="font-medium">Crane Details</span>
            </div>
            
            <div className={`h-px flex-1 ${currentStep >= 2 ? 'bg-primary' : 'bg-border'}`} />
            
            <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-primary' : 'text-text-secondary'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= 2 ? 'bg-primary text-white' : 'bg-muted text-text-secondary'
              }`}>
                2
              </div>
              <span className="font-medium">Valuation Results</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Form and Results - Main Content */}
          <div className="xl:col-span-3 space-y-6">
            {currentStep === 1 ? (
              <CraneDetailsForm 
                onSubmit={handleFormSubmit}
                isLoading={isLoading}
              />
            ) : (
              <>
                <ValuationResults 
                  results={valuationResults}
                  craneData={formData}
                  onSave={handleSaveConfiguration}
                />
                <DepreciationAnalysis 
                  results={valuationResults}
                  craneData={formData}
                />
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-1 space-y-6">
            {currentStep === 1 ? (
              <>
                {/* Valuation Tips */}
                <div className="bg-surface border border-border rounded-lg shadow-card p-4">
                  <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
                    <Icon name="Lightbulb" size={16} className="text-accent" />
                    <span>Valuation Tips</span>
                  </h3>
                  
                  <div className="space-y-4 text-sm">
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-text-primary mb-1">Accurate Hours</h4>
                      <p className="text-text-secondary">Engine hours significantly impact valuation. Use actual readings for best results.</p>
                    </div>
                    
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-text-primary mb-1">Condition Assessment</h4>
                      <p className="text-text-secondary">Be objective about condition. Include recent repairs and maintenance.</p>
                    </div>
                    
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-text-primary mb-1">Market Context</h4>
                      <p className="text-text-secondary">Regional and seasonal factors affect pricing. Consider timing of sale.</p>
                    </div>
                  </div>
                </div>

                {/* Sample Valuations */}
                <div className="bg-surface border border-border rounded-lg shadow-card p-4">
                  <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
                    <Icon name="BarChart3" size={16} className="text-primary" />
                    <span>Sample Valuations</span>
                  </h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center p-2 bg-muted rounded">
                      <span className="text-text-secondary">Grove RT890E (2020)</span>
                      <span className="font-medium text-text-primary">$850K</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-muted rounded">
                      <span className="text-text-secondary">Liebherr LTM 1130 (2019)</span>
                      <span className="font-medium text-text-primary">$920K</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-muted rounded">
                      <span className="text-text-secondary">Terex AC100/4L (2018)</span>
                      <span className="font-medium text-text-primary">$675K</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <MarketInsights craneData={formData} />
                <ValuationHistory configurations={savedConfigurations} />
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomCraneValuation;